import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { metricsAPI, manuscriptsAPI, seedAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, FileText, Layers, Eye, BarChart3, Clock, CheckCircle, XCircle, AlertCircle, Inbox, Database, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const statusIcon = { 'En Revision': Clock, 'Revisiones Requeridas': AlertCircle, 'Aceptado': CheckCircle, 'Rechazado': XCircle, 'Publicado': Layers };
const statusColor = { 'En Revision': 'text-yellow-600 bg-yellow-50', 'Revisiones Requeridas': 'text-orange-600 bg-orange-50', 'Aceptado': 'text-green-600 bg-green-50', 'Rechazado': 'text-red-600 bg-red-50', 'Publicado': 'text-blue-600 bg-blue-50' };

export default function DashboardHome() {
  const [metrics, setMetrics] = useState({});
  const [allManuscripts, setAllManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  useEffect(() => {
    Promise.all([
      isAdmin() ? metricsAPI.get().then(r => setMetrics(r.data)).catch(() => {}) : Promise.resolve(),
      manuscriptsAPI.list().then(r => setAllManuscripts(r.data.items || r.data)).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const handleSeed = async () => {
    try { const r = await seedAPI.run(); toast.success(r.data.message); setTimeout(() => window.location.reload(), 800); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  const recent = allManuscripts.slice(0, 8);
  const byStatus = {};
  allManuscripts.forEach(m => { byStatus[m.status] = (byStatus[m.status] || 0) + 1; });
  const newSubmissions = allManuscripts.filter(m => m.status === 'En Revision' && !(m.reviewers?.length || m.reviewer_id)).length;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="dashboard-home">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-dark">Panel Editorial</h1>
            <p className="text-sm font-sans text-gray-400 mt-1">Resumen del flujo editorial de Archivos Trans</p>
          </div>
          {isAdmin() && (
            <Button variant="outline" size="sm" onClick={handleSeed} className="font-sans text-xs" data-testid="seed-btn">
              <Database className="w-3 h-3 mr-1" /> Sembrar Datos Iniciales
            </Button>
          )}
        </div>

        {/* Metrics Cards */}
        {isAdmin() && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Usuaries', value: metrics.total_users, icon: Users, accent: 'bg-trans-blue/10 text-trans-blue', to: '/panel/usuarios' },
              { label: 'Manuscritos', value: metrics.total_manuscripts, icon: FileText, accent: 'bg-trans-pink/10 text-trans-pink', to: '/panel/envios' },
              { label: 'Publicados', value: metrics.total_articles, icon: Layers, accent: 'bg-green-50 text-green-600', to: '/panel/publicados' },
              { label: 'En Revision', value: metrics.pending_reviews, icon: Eye, accent: 'bg-yellow-50 text-yellow-600', to: '/panel/revision' },
            ].map(m => (
              <Link key={m.label} to={m.to} className="p-5 bg-white border border-gray-100 rounded-sm hover:border-gray-200 hover:shadow-sm transition-all group" data-testid={`metric-${m.label.toLowerCase()}`}>
                <div className={`w-9 h-9 rounded-md ${m.accent} flex items-center justify-center mb-3`}>
                  <m.icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <p className="text-2xl font-sans font-bold text-slate-dark">{m.value || 0}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs font-sans text-gray-400">{m.label}</p>
                  <ArrowRight className="w-3 h-3 text-gray-300 group-hover:text-trans-blue transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pipeline Overview */}
        <div className="grid lg:grid-cols-5 gap-3 mb-8">
          {[
            { label: 'Nuevos Envios', value: newSubmissions, status: 'En Revision', to: '/panel/envios', icon: Inbox },
            { label: 'En Revision', value: byStatus['En Revision'] || 0, status: 'En Revision', to: '/panel/revision', icon: Eye },
            { label: 'Revisiones Req.', value: byStatus['Revisiones Requeridas'] || 0, status: 'Revisiones Requeridas', to: '/panel/envios', icon: AlertCircle },
            { label: 'Aceptados', value: byStatus['Aceptado'] || 0, status: 'Aceptado', to: '/panel/dictaminados', icon: CheckCircle },
            { label: 'Publicados', value: byStatus['Publicado'] || 0, status: 'Publicado', to: '/panel/publicados', icon: Layers },
          ].map(s => (
            <Link key={s.label} to={s.to} className="p-4 bg-white border border-gray-100 rounded-sm text-center hover:border-gray-200 transition-all" data-testid={`pipeline-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <s.icon className={`w-5 h-5 mx-auto mb-2 ${statusColor[s.status]?.split(' ')[0] || 'text-gray-400'}`} strokeWidth={1.5} />
              <p className="text-xl font-sans font-bold text-slate-dark">{s.value}</p>
              <p className="text-[10px] font-sans text-gray-400 mt-0.5">{s.label}</p>
            </Link>
          ))}
        </div>

        {/* Recent manuscripts */}
        <div className="bg-white border border-gray-100 rounded-sm">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-serif font-semibold text-slate-dark">Manuscritos Recientes</h2>
            <Link to="/panel/envios" className="text-xs font-sans text-trans-blue hover:underline flex items-center gap-1">Ver todos <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {recent.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox className="w-10 h-10 text-gray-200 mx-auto mb-3" strokeWidth={1} />
              <p className="text-sm font-sans text-gray-400">No hay manuscritos recibidos aun.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recent.map((m, i) => {
                const Icon = statusIcon[m.status] || Clock;
                return (
                  <div key={m.id} className="px-5 py-3 flex items-center gap-4 hover:bg-gray-50/50 transition-colors" data-testid={`recent-manuscript-${i}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${statusColor[m.status] || 'bg-gray-50 text-gray-400'}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-sans font-medium text-slate-dark truncate">{m.titulo}</p>
                      <p className="text-xs font-sans text-gray-400">{m.autor_name} &middot; {new Date(m.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] font-sans shrink-0 ${statusColor[m.status] || ''}`}>{m.status}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
